import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { StatsCard } from '../components/ui/StatsCard';

const ICON = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const fmt = (n: any) => n == null ? '—' : Number(n).toLocaleString();
const when = (s: string | null) => s ? new Date(s).toLocaleString() : '—';

type NormalEvent = {
  id: string;
  when: string;
  event_type: string;
  provider: string;
  person_name: string | null;
  person_id: string | null;
  property_id: string | null;
  property_name: string | null;
  quantity: string;            // includes unit
  cost_usd: number | null;
  path: string | null;
  label: string | null;        // friendly name for path UUIDs
  extra: string | null;        // extra context (door, status, duration, etc.)
};

const SOURCES = ['ledger', 'calls', 'visitor'] as const;
type SourceKey = typeof SOURCES[number];

// Normalize wildly-different source shapes (calls in DDB, visitor summary,
// usage_ledger ingest) into one event_type/quantity/property/person row so
// the table can render uniformly.
function normalize(source: SourceKey, raw: any[], propMap: Map<string, string>, personMap: Map<string, string>): NormalEvent[] {
  if (source === 'ledger') {
    return raw.map((e: any, i) => ({
      id: e.id || `${e.event_type}-${i}`,
      when: e.occurred_at || e.created_at || '',
      event_type: e.event_type,
      provider: e.provider || '—',
      person_id: e.person_id || null,
      person_name: e.person_name || (e.person_id ? propMap.get(String(e.person_id).toLowerCase()) || null : null),
      property_id: e.property_id || null,
      property_name: e.property_name || (e.property_id ? propMap.get(String(e.property_id).toLowerCase()) || null : null),
      quantity: e.quantity != null ? `${e.quantity} ${e.unit_label || ''}`.trim() : '—',
      cost_usd: e.row_cost_usd != null ? Number(e.row_cost_usd) : (e.total_cost_usd != null ? Number(e.total_cost_usd) : null),
      path: null, label: null,
      extra: e.feature_key || null,
    }));
  }
  if (source === 'calls') {
    return raw.map((e: any) => {
      const dur = Number(e.duration || e.duration_seconds || 0);
      return {
        id: e.id || e.uuid || `${e.legacy_id || ''}-${e.created_at || ''}`,
        when: e.created_at || e.answered_at || e.ended_at || '',
        event_type: `${e.call_type || 'voice'}.${e.status || 'completed'}`,
        provider: e.call_type === 'video' ? 'LiveKit' : 'Telnyx',
        person_id: e.tenant_id || null,
        person_name: e.tenant_name || (e.tenant_id ? personMap.get(String(e.tenant_id).toLowerCase()) || null : null),
        property_id: e.property_id || null,
        property_name: e.property_id ? propMap.get(String(e.property_id).toLowerCase()) || null : null,
        quantity: dur > 0 ? `${dur} sec (${(dur/60).toFixed(1)} min)` : '—',
        cost_usd: e.cost_usd != null ? Number(e.cost_usd)
                : e.cost != null ? Number(e.cost)
                : (e.call_type !== 'video' && dur > 0)
                    ? Math.ceil(dur / 60) * 0.007  // Telnyx fallback ~$0.007/min
                    : null,
        path: null, label: null,
        extra: [
          e.from_number ? `from ${e.from_number}` : null,
          e.to_number ? `to ${e.to_number}` : null,
          e.unit_number ? `unit ${e.unit_number}` : null,
          e.door_unlocked ? `door:${e.door_name || 'unlocked'}` : null,
        ].filter(Boolean).join(' · ') || null,
      };
    });
  }
  // visitor
  return raw.map((e: any, i) => ({
    id: `${e.when || ''}-${i}`,
    when: e.when || '',
    event_type: 'page_view',
    provider: e.domain || 'tracker',
    person_id: e.user_id || null,
    person_name: null,
    property_id: null,
    property_name: null,
    quantity: '—',
    cost_usd: null,
    path: e.path || null,
    label: e.label || null,
    extra: [e.device, e.browser].filter(Boolean).join(' · ') || null,
  }));
}

export const EventHistory: React.FC = () => {
  const [source, setSource] = useState<SourceKey>('ledger');
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<NormalEvent[]>([]);
  const [filter, setFilter] = useState('');
  const [error, setError]  = useState<string | null>(null);
  const [propMap,   setPropMap]   = useState<Map<string, string>>(new Map());
  const [personMap, setPersonMap] = useState<Map<string, string>>(new Map());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
  const token = localStorage.getItem('intrlock_token') || '';
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  // Build UUID → name maps once so every source can resolve property + tenant
  // names without each row carrying them.
  useEffect(() => {
    Promise.all([
      fetch(`${API}/properties`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API}/tenants`,    { headers }).then(r => r.json()).catch(() => []),
    ]).then(([props, people]) => {
      const pm = new Map<string, string>();
      for (const p of (props || [])) {
        const id = String(p.uuid || p.id || '').toLowerCase();
        if (id) pm.set(id, p.name || `Property ${id.slice(0,8)}`);
      }
      setPropMap(pm);
      const tm = new Map<string, string>();
      for (const t of (people || [])) {
        const id = String(t.uuid || t.id || '').toLowerCase();
        if (id) tm.set(id, `${t.first_name || ''} ${t.last_name || ''}`.trim() || `Person ${id.slice(0,8)}`);
      }
      setPersonMap(tm);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setLoading(true); setError(null);
    setPage(1);
    const url = source === 'visitor' ? `${API}/events/visitor/summary`
              : source === 'ledger'  ? `${API}/billing-events-log?limit=500`
              :                        `${API}/events/calls?limit=1000`;
    fetch(url, { headers })
      .then(r => r.json())
      .then(d => {
        const raw = source === 'visitor' ? (d.recent_views || [])
                  : source === 'ledger'  ? (d.recent || [])
                  :                        (Array.isArray(d) ? d : (d.items || []));
        setEvents(normalize(source, raw, propMap, personMap));
      })
      .catch(e => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [source, propMap, personMap]);

  const filtered = useMemo(() => {
    if (!filter) return events;
    const f = filter.toLowerCase();
    return events.filter(e => JSON.stringify(e).toLowerCase().includes(f));
  }, [events, filter]);

  useEffect(() => { setPage(1); }, [filter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filtered, safePage, pageSize]
  );
  const totalCost = useMemo(
    () => filtered.reduce((s, e) => s + (e.cost_usd ?? 0), 0),
    [filtered]
  );

  return (
    <div className="shadow-page-frame legacy-dashboard-migrated space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Event History</h1>
        <p className="text-gray-500 mt-1">Cross-source log of everything that happened: ledger writes, visitor page views, call events. Filterable.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard title="Total Events"  value={fmt(events.length)} icon={ICON} />
        <StatsCard title="After Filter"  value={fmt(filtered.length)} icon={ICON} />
        <StatsCard title="Source"        value={source === 'ledger' ? 'Usage Ledger' : source === 'calls' ? 'Call Events' : 'Visitor / Tracker'} icon={ICON} />
        <StatsCard title="Total Cost"    value={totalCost > 0 ? `$${totalCost.toFixed(4)}` : '—'} icon={ICON} />
      </div>

      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 pb-3">
        {SOURCES.map(s => {
          const label = s === 'ledger' ? 'Usage Ledger' : s === 'calls' ? 'Call Events' : 'Visitor / Tracker';
          return (
            <button key={s} onClick={() => setSource(s)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${source === s
                ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {label}
            </button>
          );
        })}
        <div className="flex-1" />
        <input
          value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="Filter event_type, person, property…"
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-72"
        />
      </div>

      <Card padding={false}>
        <CardHeader title="Recent Events" subtitle={`Most recent rows from /${source}`} />
        {loading ? <div className="p-6 text-sm text-gray-400">Loading…</div>
         : error ? <div className="p-6 text-sm text-red-500">Failed to load: {error}</div>
         : filtered.length === 0 ? <div className="p-6 text-sm text-gray-400">No events.</div>
         : (
          <>
          <Table
            data={paged.map((e, i) => ({ ...e, id: e.id || `${e.event_type}-${i}` }))}
            columns={[
              { key: 'when',       header: 'When',     render: (r: any) => <span className="text-xs text-gray-600">{when(r.when)}</span> },
              { key: 'event_type', header: 'Event',    render: (r: any) => <span className="font-mono text-xs">{r.event_type}</span> },
              { key: 'provider',   header: 'Provider', render: (r: any) => <span className="text-xs">{r.provider}</span> },
              { key: 'person',     header: 'Person',   render: (r: any) => r.person_name
                ? <div className="text-xs">
                    <span className="font-medium text-gray-900">{r.person_name}</span>
                    {r.person_id && <span className="block font-mono text-[10px] text-gray-400">{String(r.person_id).slice(0,8)}</span>}
                  </div>
                : <span className="text-xs text-gray-400">—</span> },
              { key: 'property',   header: 'Property', render: (r: any) => r.property_name
                ? <div className="text-xs">
                    <span className="font-medium">{r.property_name}</span>
                    {r.property_id && <span className="block font-mono text-[10px] text-gray-400">{String(r.property_id).slice(0,8)}</span>}
                  </div>
                : r.property_id ? <span className="font-mono text-[10px] text-gray-400">{String(r.property_id).slice(0,8)}</span>
                : <span className="text-xs text-gray-400">—</span> },
              { key: 'qty',        header: 'Qty / Duration', render: (r: any) => <span className="font-mono text-xs">{r.quantity}</span> },
              { key: 'cost',       header: 'Cost',     render: (r: any) => r.cost_usd != null
                ? <span className="font-mono text-xs">${Number(r.cost_usd).toFixed(6)}</span>
                : <span className="text-xs text-gray-400">—</span> },
              { key: 'extra',      header: 'Details',  render: (r: any) => r.path
                ? <div className="max-w-[260px]">
                    <span className="font-mono text-[10px] truncate block">{r.path}</span>
                    {r.label && <span className="block text-[10px] text-emerald-700">{r.label}</span>}
                  </div>
                : r.extra
                  ? <span className="text-[10px] text-gray-500">{r.extra}</span>
                  : <span className="text-xs text-gray-400">—</span> },
            ]}
          />
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 text-xs text-gray-600">
            <div>
              Showing {filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1}–
              {Math.min(safePage * pageSize, filtered.length)} of {fmt(filtered.length)}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-gray-500">Page size</label>
              <select
                value={pageSize}
                onChange={e => setPageSize(parseInt(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-xs"
              >
                {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <button onClick={() => setPage(1)} disabled={safePage <= 1}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">‹</button>
              <span>
                Page <input type="number" min={1} max={totalPages} value={safePage}
                  onChange={e => setPage(Math.max(1, Math.min(totalPages, parseInt(e.target.value) || 1)))}
                  className="w-14 border border-gray-300 rounded px-2 py-1 text-xs text-center" /> / {totalPages}
              </span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">›</button>
              <button onClick={() => setPage(totalPages)} disabled={safePage >= totalPages}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40">»</button>
            </div>
          </div>
          </>
        )}
      </Card>
    </div>
  );
};
